import { z } from 'zod'

/**
 * Kanban configuravel (protótipo v2 + SPEC-20260807-1610).
 *
 * Cadeia de elegibilidade:
 *   Tipo de demanda -> Demanda -> Item de Trabalho -> Status -> Lane(s)
 *
 * Regras do produto:
 *  - cada lane tem NO MAXIMO UM status — e o status que o item assume ao ser
 *    solto nela;
 *  - lane SEM status e valida: soltar um cartao nela MANTEM o status do item;
 *    ela exibe os itens elegiveis cujo status nao esta em nenhuma lane com
 *    status (absorve os "nao mapeados");
 *  - o MESMO status pode estar em varias lanes do mesmo quadro; o cartao
 *    aparece em todas elas;
 *  - item SEM demanda aparece em qualquer quadro (nao pertence a nenhum tipo);
 *  - o quadro nao "contem" cartoes: eles sao derivados por consulta.
 */

/** Como os filhos de um item aparecem no quadro. */
export const childDisplayModeSchema = z.enum([
  /** Cada filho aparece como cartao proprio, conforme o seu status. */
  'INDEPENDENT',
  /** O pai mostra progresso e lista expansivel; filhos nao viram cartoes. */
  'GROUPED',
  /** So itens raiz viram cartoes; descendentes ficam no detalhe. */
  'ROOT_ONLY',
])
export type ChildDisplayMode = z.infer<typeof childDisplayModeSchema>

export const kanbanColumnSchema = z.object({
  id: z.string().uuid(),
  boardId: z.string().uuid(),
  code: z.string().min(1).max(64),
  /** Nome exibido (texto livre editado inline no modo de edicao do quadro). */
  name: z.string().min(1).max(200),
  position: z.number().int().nonnegative(),
  /** O unico status desta lane. null = lane SEM status (mantem o status do
   *  item ao receber cartao e mostra os status nao mapeados). */
  statusId: z.string().uuid().nullable(),
  /** null = sem limite. */
  wipLimit: z.number().int().positive().nullable(),
  isInitial: z.boolean(),
  isFinal: z.boolean(),
  isActive: z.boolean(),
})
export type KanbanColumn = z.infer<typeof kanbanColumnSchema>

export const kanbanBoardSchema = z.object({
  id: z.string().uuid(),
  code: z.string().min(1).max(64),
  nameTranslationKey: z.string().min(1),
  descriptionTranslationKey: z.string().nullable(),
  companyAreaId: z.string().uuid().nullable(),
  childDisplayMode: childDisplayModeSchema,
  /** Tipos de demanda que participam deste quadro. */
  demandTypeIds: z.array(z.string().uuid()),
  isActive: z.boolean(),
  isDefault: z.boolean(),
  archivedAt: z.coerce.date().nullable(),
})
export type KanbanBoard = z.infer<typeof kanbanBoardSchema>

// --- Elegibilidade -------------------------------------------------------

export type EligibilityResult =
  /** Elegivel: aparece em TODAS estas lanes (mesmo status pode se repetir). */
  | { eligible: true; columnIds: readonly string[] }
  /** Elegivel pela demanda, mas o status atual nao esta em nenhuma lane.
   *  Nao pode sumir: vai para a area administrativa "Itens nao mapeados". */
  | { eligible: false; reason: 'unmapped-status' }
  | { eligible: false; reason: 'no-demand' }
  | { eligible: false; reason: 'demand-type-not-in-board' }
  | { eligible: false; reason: 'archived' }

export function isEligible(
  workItem: {
    demandId: string | null
    demandTypeId: string | null
    workItemStatusId: string
    archivedAt: Date | null
  },
  board: { demandTypeIds: readonly string[] },
  columns: readonly Pick<KanbanColumn, 'id' | 'statusId' | 'isActive'>[],
): EligibilityResult {
  if (workItem.archivedAt !== null) return { eligible: false, reason: 'archived' }
  // Item SEM demanda participa de qualquer quadro: ele nao pertence a tipo
  // algum, entao nao pode ser excluido pelo filtro de tipos de demanda.
  if (workItem.demandId !== null && workItem.demandTypeId !== null && !board.demandTypeIds.includes(workItem.demandTypeId)) {
    return { eligible: false, reason: 'demand-type-not-in-board' }
  }
  const lanes = columns.filter((c) => c.isActive && c.statusId === workItem.workItemStatusId)
  if (lanes.length > 0) return { eligible: true, columnIds: lanes.map((c) => c.id) }
  // status fora das lanes: cai nas lanes SEM status, se existirem
  const statusless = columns.filter((c) => c.isActive && c.statusId === null)
  if (statusless.length > 0) return { eligible: true, columnIds: statusless.map((c) => c.id) }
  return { eligible: false, reason: 'unmapped-status' }
}

/** Status que um cartao assume ao ser solto na lane. null = mantem o atual. */
export function statusForDrop(column: Pick<KanbanColumn, 'statusId'>): string | null {
  return column.statusId
}

// --- Validacao da configuracao ------------------------------------------

export type KanbanValidationIssue = { code: 'no-active-column' }

/**
 * Valida o quadro antes de salvar/ativar a configuracao.
 *
 * Lane sem status e valida (mantem o status do item ao receber cartao) e as
 * restricoes do quadro (tipos de item, demandas, membros) sao OPCIONAIS —
 * nenhuma preenchida significa "mostrar todos os itens". So a ausencia de
 * lane ativa impede o quadro de funcionar.
 */
export function validateBoard(
  columns: readonly Pick<KanbanColumn, 'id' | 'statusId' | 'isActive'>[],
): KanbanValidationIssue[] {
  const issues: KanbanValidationIssue[] = []
  if (columns.filter((c) => c.isActive).length === 0) issues.push({ code: 'no-active-column' })
  return issues
}

/** Estado do limite de WIP de uma lane, para o alerta visual no header. */
export function wipState(
  column: Pick<KanbanColumn, 'wipLimit'>,
  cardCount: number,
): 'none' | 'ok' | 'near' | 'over' {
  if (column.wipLimit === null) return 'none'
  if (cardCount > column.wipLimit) return 'over'
  if (cardCount >= column.wipLimit) return 'near'
  return 'ok'
}
